import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';
import LanguageSwitcher from '../LanguageSwitcher';

const Header = () => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu when route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    // Close menu on escape key
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && isMenuOpen) {
            setIsMenuOpen(false);
        }
    }, [isMenuOpen]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const navigation = [
        { name: t('header.home'), href: '/', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
        )},
        { name: t('header.contact'), href: '/contact', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        )},
    ];

    const isActive = (href) => {
        if (href === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(href);
    };

    const isHome = location.pathname === '/';
    const isTransparent = isHome && !isScrolled && !isMenuOpen;

    return (
        <>
            <header className={`fixed top-0 w-full z-50 transition-all duration-300 safe-area-top ${isTransparent
                ? 'bg-transparent'
                : 'bg-white/95 shadow-lg border-b border-primary-100/50 backdrop-blur-md'
                }`}>
                <nav className="px-4 sm:px-6 md:px-16 py-2">
                    <div className="flex items-center justify-between h-14 md:h-16 w-full">
                        {/* Logo */}
                        <div className="flex items-center flex-shrink-0">
                            <Link to="/" className="flex items-center group touch-feedback" onClick={() => setIsMenuOpen(false)}>
                                <div className="relative">
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-16 md:h-16 flex items-center justify-center mr-2 md:mr-3 transition-all duration-300 transform group-hover:scale-105">
                                        <img
                                            src="/favicon.svg"
                                            alt="Ablex Logo"
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <span className={`text-lg sm:text-xl md:text-3xl font-bold transition-colors duration-300 ${isTransparent ? 'text-white' : 'text-black'}`}>
                                        {t('header.companyName')}
                                    </span>
                                    <span className={`text-[10px] sm:text-xs md:text-sm font-medium hidden sm:block transition-colors duration-300 ${isTransparent ? 'text-gray-200' : 'text-gray-600'}`}>
                                        {t('header.companySlogan')}
                                    </span>
                                </div>
                            </Link>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
                            <LanguageSwitcher />
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`px-4 py-2 rounded-full text-base lg:text-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isActive(item.href)
                                        ? 'text-white bg-gradient-to-r from-blue-600 to-blue-500 shadow-md'
                                        : isTransparent 
                                            ? 'text-white hover:text-gray-200 hover:bg-white/10' 
                                            : 'text-neutral-700 hover:text-primary-700 hover:bg-primary-50'
                                        }`}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="flex items-center space-x-2 md:hidden">
                            <div className="scale-[0.8] sm:scale-90">
                                <LanguageSwitcher isMobile={true} />
                            </div>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className={`inline-flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 touch-target ${
                                    isTransparent 
                                        ? 'text-white hover:bg-white/10' 
                                        : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                                aria-expanded={isMenuOpen}
                            >
                                <div className="relative w-6 h-6">
                                    <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 top-3' : 'top-1'}`}></span>
                                    <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 top-3 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                                    <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 top-3' : 'top-5'}`}></span>
                                </div>
                            </button>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Mobile Navigation Overlay */}
            <div 
                className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setIsMenuOpen(false)}
                aria-hidden="true"
            />

            {/* Mobile Navigation Panel */}
            <div 
                className={`fixed top-[72px] left-0 right-0 z-40 md:hidden transition-all duration-300 transform ${isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}
            >
                <div className="mx-4 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden mobile-menu-enter">
                    <nav className="p-4 space-y-2">
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`flex items-center px-4 py-4 rounded-xl text-base font-medium transition-all duration-200 touch-target ${isActive(item.href)
                                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-500 shadow-md'
                                    : 'text-neutral-700 hover:text-primary-700 hover:bg-primary-50 active:bg-primary-100'
                                    }`}
                                onClick={() => setIsMenuOpen(false)}
                            >
                                <span className={`mr-3 ${isActive(item.href) ? 'text-white' : 'text-primary-500'}`}>
                                    {item.icon}
                                </span>
                                {item.name}
                                <svg className="w-5 h-5 ml-auto opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        ))}
                    </nav>
                    
                    {/* Quick contact info in mobile menu */}
                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                        <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            alex@buergstaller.it
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Header;
